import { ControlType, FieldBase } from '@vendasta/store';
import { getResellerItemUniqueId, isAddon } from '../../../core/reseller';
import { CommonFormSection, ResellerItemOrderForm } from '../../order-form-page';
import { ActivatableItem, SelectedItems } from '../interface';
import { Order, OrderType } from './interface';

export function combineArrays<T>(arr1: T[], arr2: T[]): T[] {
  return (arr1 || []).concat(arr2 || []);
}

export function convertActivatableItemsIntoOrders(
  businessId: string,
  activatableItems: ActivatableItem[],
  selectedItems: SelectedItems,
): Order[] {
  if (
    !activatableItems ||
    activatableItems.length === 0 ||
    !selectedItems ||
    Array.from(selectedItems.keys()).length === 0
  ) {
    return [];
  }
  const selectedItemIds = Array.from(selectedItems.keys());
  const activatableItemMap = activatableItemArrayToMap(activatableItems);

  const orders = [];
  selectedItemIds.forEach((id) => {
    const quantity = selectedItems.get(id) ? selectedItems.get(id).quantity : 0;
    const activatableItem = activatableItemMap.get(id);
    if (!activatableItem || quantity <= 0) {
      return;
    }
    const order = {
      appId: activatableItem.appId,
      parentAppId: activatableItem.parentAppId,
      businessId: businessId,
      name: activatableItem.name,
      type: activatableItem.parentAppId ? 'ADDON' : 'APPLICATION',
      commonFields: [],
      customFields: [],
      usesOrderForm: activatableItem.usesOrderForm,
    } as Order;

    if (selectedItems.get(id) && selectedItems.get(id).quantity) {
      order.editionId = selectedItems.get(id).editionId;
    }

    orders.push(...duplicateItems(order, quantity));
  });
  return orders;
}

export function convertResellerItemOrderFormToOrders(
  businessId: string,
  resellerItemOrders: ResellerItemOrderForm[],
  selectedItems: SelectedItems,
): Order[] {
  const resellerItemOrdersById = groupResellerItemOrdersById(resellerItemOrders);
  if (!resellerItemOrdersById) {
    return [];
  }
  return Array.from(resellerItemOrdersById.entries())
    .map(([resellerItemId, resellerItems]) => {
      let quantity = 0;
      let editionId;
      if (selectedItems && selectedItems.get(resellerItemId)) {
        const selectedItem = selectedItems.get(resellerItemId);
        quantity = selectedItem.quantity;
        editionId = selectedItem.editionId;
      }
      return buildOrders(businessId, resellerItems, quantity, editionId);
    })
    .reduce((flatArray, arr) => {
      flatArray.push(...arr);
      return flatArray;
    }, [])
    .filter(Boolean);
}

function buildOrders(
  businessId: string,
  resellerOrders: ResellerItemOrderForm[],
  quantity: number,
  editionId?: string,
): Order[] {
  if (quantity <= 0) {
    return [];
  }
  if (resellerOrders.length === 1 && quantity > 1) {
    // There is only a single order form for multiple addon activations
    return getOrderFromResellerItemsWithQuantity(businessId, resellerOrders[0], quantity, editionId);
  } else if (resellerOrders.length >= quantity) {
    // There is one order form per activation (multi-activate addon with multiple forms)
    return resellerOrders
      .map((resellerItem) => getOrderFromResellerItemsWithQuantity(businessId, resellerItem, 1, editionId))
      .filter(Boolean)
      .reduce((acc, curr) => {
        acc.push(...curr);
        return acc;
      }, []);
  } else {
    return [];
  }
}

export function groupResellerItemOrdersById(
  resellerItemOrderForms: ResellerItemOrderForm[],
): Map<string, ResellerItemOrderForm[]> {
  return (resellerItemOrderForms || []).reduce((m, curr) => {
    const resellerItemId = getResellerItemUniqueId(curr);
    const currentArray = m.get(resellerItemId) || [];
    m.set(resellerItemId, currentArray.concat(curr));
    return m;
  }, new Map<string, ResellerItemOrderForm[]>());
}

export function getOrderFromResellerItem(
  businessId: string,
  resellerItemOrder: ResellerItemOrderForm,
  editionId?: string,
): Order {
  if (!resellerItemOrder) {
    return undefined;
  }
  const commonFields = flattenCommonFormSectionFields(resellerItemOrder.commonFields || []);
  const customFields = resellerItemOrder.customFields || [];

  let parentAppId: string;
  let appId: string;
  if (isAddon(resellerItemOrder)) {
    appId = resellerItemOrder.addonId;
    parentAppId = resellerItemOrder.appId;
  } else {
    appId = resellerItemOrder.appId;
  }
  return {
    appId: appId,
    parentAppId: parentAppId,
    businessId: businessId,
    name: resellerItemOrder.name,
    type: isAddon(resellerItemOrder) ? OrderType.ADDON : OrderType.APPLICATION,
    commonFields: commonFields,
    customFields: customFields,
    usesOrderForm: resellerItemOrder.usesCustomizationForm || resellerItemOrder.usesActivationSpecificEntryUrl,
    editionId: editionId,
  };
}

export function duplicateItems<T>(item: T, quantity: number): T[] {
  if (quantity <= 0) {
    return [];
  }
  const items: T[] = [];
  for (let i = 0; i < quantity; i++) {
    items.push({ ...(item as any) });
  }
  return items;
}

export function getOrderFromResellerItemsWithQuantity(
  businessId: string,
  resellerItemOrder: ResellerItemOrderForm,
  quantity: number,
  editionId?: string,
): Order[] {
  if (!resellerItemOrder) {
    return [];
  }
  const order = getOrderFromResellerItem(businessId, resellerItemOrder, editionId);
  return duplicateItems(order, quantity);
}

function flattenCommonFormSectionFields(commonFormSections: CommonFormSection[]): FieldBase<any>[] {
  if (!commonFormSections) {
    return [];
  }
  const commonFormFields: FieldBase<any>[] = [];
  commonFormSections.forEach((section) => commonFormFields.push(...section.fields));
  return commonFormFields;
}

interface OrderFormField {
  field_id: string;
  field_type: ControlType;
  answer: unknown;
  description: string;
  label: string;
}

// convert order into api map
export function convertOrderFormFields(orderFormFields: FieldBase<any>[]): OrderFormField[] {
  if (!orderFormFields) {
    return [];
  }
  return orderFormFields.map((orderFormField) => {
    return <OrderFormField>{
      field_id: orderFormField.id,
      field_type: orderFormField.controlType,
      answer: orderFormField.value,
      description: orderFormField.description,
      label: orderFormField.label,
    };
  });
}

export function activatableItemArrayToMap(activatableItems: ActivatableItem[]): Map<string, ActivatableItem> {
  return (activatableItems || []).reduce((m, curr) => {
    m.set(curr.appId, curr);
    return m;
  }, new Map<string, ActivatableItem>());
}
